import React, { useState, useMemo } from 'react';
import {
  Card, Button, Input, Space, Tag, Progress, Collapse,
  Row, Col, Typography, Tabs, Alert,
  Checkbox, Divider, Badge,
} from 'antd';
import {
  BookOutlined, CheckCircleOutlined, ClockCircleOutlined,
  SearchOutlined, TrophyOutlined, BulbOutlined,
  RocketOutlined,
  StarOutlined, InfoCircleOutlined, ReadOutlined,
  CheckOutlined, VideoCameraOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { VideoTutorials } from './LearningVideos';
import { atriumTokens } from '../../../theme/theme';
import {
  TUTORIALS, CATEGORY_COLORS, DIFFICULTY_COLORS,
  ROLE_DISPLAY, LEARNING_PATHS, QUICK_REF_SECTIONS,
  type Tutorial as TutorialItem, type TutorialProgress, type ProgressMap,
} from '../content/tutorialContent';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const STORAGE_KEY = 'cm_tutorial_progress';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const loadProgress = (): ProgressMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveProgress = (map: ProgressMap) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* noop */ }
};

// ─── TutorialCard Component ───────────────────────────────────────────────────

interface TutorialCardProps {
  tutorial: TutorialItem;
  progress: TutorialProgress;
  onStepToggle: (tutorialId: string, stepIdx: number) => void;
  onMarkComplete: (tutorialId: string) => void;
}

const TutorialCard: React.FC<TutorialCardProps> = ({ tutorial, progress, onStepToggle, onMarkComplete }) => {
  const pct = tutorial.steps.length > 0
    ? Math.round((progress.stepsCompleted.length / tutorial.steps.length) * 100)
    : 0;
  const borderColor = CATEGORY_COLORS[tutorial.category] ?? atriumTokens.blue;

  return (
    <Card
      style={{
        borderLeft: `4px solid ${borderColor}`,
        marginBottom: 16,
        boxShadow: atriumTokens.sh,
      }}
      bodyStyle={{ padding: 0 }}
    >
      <Collapse ghost>
        <Panel
          key="1"
          header={
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%' }}>
              <div style={{
                width: 40, height: 40, borderRadius: 8, background: `${borderColor}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: borderColor, fontSize: 18, flexShrink: 0,
              }}>
                {tutorial.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {progress.completed && (
                    <CheckCircleOutlined style={{ color: atriumTokens.ok, fontSize: 16 }} />
                  )}
                  <Text strong style={{ fontSize: 15, color: progress.completed ? atriumTokens.ok : atriumTokens.ink }}>
                    {tutorial.title}
                  </Text>
                  <Tag color={DIFFICULTY_COLORS[tutorial.difficulty]} style={{ fontSize: 11, margin: 0 }}>
                    {tutorial.difficulty}
                  </Tag>
                  <Tag icon={<ClockCircleOutlined />} style={{ fontSize: 11, margin: 0, color: atriumTokens.ink2, borderColor: atriumTokens.line2 }}>
                    {tutorial.estimatedTime}
                  </Tag>
                </div>
                <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 2 }}>
                  {tutorial.description}
                </Text>
                <div style={{ marginTop: 8 }}>
                  <Progress
                    percent={pct}
                    size="small"
                    strokeColor={borderColor}
                    trailColor={atriumTokens.line}
                    format={p => (
                      <Text style={{ fontSize: 11, color: atriumTokens.mut }}>
                        {progress.stepsCompleted.length}/{tutorial.steps.length}
                      </Text>
                    )}
                  />
                </div>
              </div>
            </div>
          }
        >
          <div style={{ padding: '0 16px 16px 16px' }}>
            {tutorial.steps.map((step, idx) => {
              const done = progress.stepsCompleted.includes(idx);
              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex', gap: 12, marginBottom: 20,
                    opacity: done ? 0.75 : 1,
                  }}
                >
                  <div style={{ paddingTop: 2, flexShrink: 0 }}>
                    <Checkbox
                      checked={done}
                      onChange={() => onStepToggle(tutorial.id, idx)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%',
                        background: done ? atriumTokens.ok : borderColor,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0,
                      }}>
                        {done ? <CheckOutlined style={{ fontSize: 10 }} /> : idx + 1}
                      </div>
                      <Text strong style={{ fontSize: 14, textDecoration: done ? 'line-through' : 'none', color: done ? atriumTokens.mut : atriumTokens.ink }}>
                        {step.title}
                      </Text>
                    </div>
                    <Paragraph style={{ marginBottom: 6, fontSize: 13, color: atriumTokens.ink2, lineHeight: 1.6 }}>
                      {step.content}
                    </Paragraph>
                    {step.navigate && (
                      <Alert
                        style={{ marginBottom: 6, padding: '4px 10px', fontSize: 12 }}
                        message={<><Text strong>Navigate to:</Text> {step.navigate}</>}
                        type="info"
                        showIcon={false}
                      />
                    )}
                    {step.tip && (
                      <Alert
                        icon={<BulbOutlined />}
                        style={{ marginBottom: 6, padding: '4px 10px', fontSize: 12 }}
                        message={<><Text strong>Pro Tip:</Text> {step.tip}</>}
                        type="success"
                        showIcon
                      />
                    )}
                    {step.warning && (
                      <Alert
                        style={{ marginBottom: 6, padding: '4px 10px', fontSize: 12 }}
                        message={<><Text strong>Important:</Text> {step.warning}</>}
                        type="warning"
                        showIcon
                      />
                    )}
                  </div>
                </div>
              );
            })}
            <Divider style={{ margin: '8px 0 12px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space wrap>
                {tutorial.tags.map(t => (
                  <Tag key={t} style={{ fontSize: 11 }}>{t}</Tag>
                ))}
              </Space>
              <Button
                type={progress.completed ? 'default' : 'primary'}
                size="small"
                icon={progress.completed ? <CheckCircleOutlined /> : <TrophyOutlined />}
                onClick={() => onMarkComplete(tutorial.id)}
                style={progress.completed ? { borderColor: atriumTokens.ok, color: atriumTokens.ok } : {}}
              >
                {progress.completed ? 'Completed' : 'Mark Complete'}
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
    </Card>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const Tutorial: React.FC = () => {
  const { user } = useAuthStore();
  const role = user?.role ?? 'admin';

  const [progressMap, setProgressMap]       = useState<ProgressMap>(loadProgress);
  const [search, setSearch]                 = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('ALL');
  const [activeTab, setActiveTab]           = useState('path');

  // Tutorials visible to this role
  const roleTutorials = useMemo(
    () => TUTORIALS.filter(t => t.roles.includes(role)),
    [role],
  );

  // Learning path for this role (ordered)
  const learningPath = useMemo(() => {
    const ids = LEARNING_PATHS[role] ?? LEARNING_PATHS.admin;
    return ids.map(id => TUTORIALS.find(t => t.id === id)).filter(Boolean) as TutorialItem[];
  }, [role]);

  // All categories for this role
  const categories = useMemo(() => {
    const cats = Array.from(new Set(roleTutorials.map(t => t.category)));
    return ['ALL', ...cats];
  }, [roleTutorials]);

  // Filtered tutorials for library tab
  const filteredTutorials = useMemo(() => {
    return roleTutorials.filter(t => {
      const matchCat  = selectedCategory === 'ALL' || t.category === selectedCategory;
      const matchDiff = selectedDifficulty === 'ALL' || t.difficulty === selectedDifficulty;
      const matchSearch = !search ||
        t.title.toLowerCase().includes(search.toLowerCase()) ||
        t.description.toLowerCase().includes(search.toLowerCase()) ||
        t.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()));
      return matchCat && matchDiff && matchSearch;
    });
  }, [roleTutorials, selectedCategory, selectedDifficulty, search]);

  // Stats
  const totalCount     = roleTutorials.length;
  const completedCount = roleTutorials.filter(t => progressMap[t.id]?.completed).length;
  const inProgressCount = roleTutorials.filter(t => {
    const p = progressMap[t.id];
    return p && p.stepsCompleted.length > 0 && !p.completed;
  }).length;
  const overallPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Handlers
  const handleStepToggle = (tutId: string, stepIdx: number) => {
    setProgressMap(prev => {
      const cur = prev[tutId] ?? { completed: false, stepsCompleted: [] };
      const already = cur.stepsCompleted.includes(stepIdx);
      const updated: TutorialProgress = {
        ...cur,
        stepsCompleted: already
          ? cur.stepsCompleted.filter(i => i !== stepIdx)
          : [...cur.stepsCompleted, stepIdx],
      };
      const next = { ...prev, [tutId]: updated };
      saveProgress(next);
      return next;
    });
  };

  const handleMarkComplete = (tutId: string) => {
    const tut = TUTORIALS.find(t => t.id === tutId);
    if (!tut) return;
    setProgressMap(prev => {
      const cur = prev[tutId] ?? { completed: false, stepsCompleted: [] };
      const nowComplete = !cur.completed;
      const updated: TutorialProgress = {
        completed:      nowComplete,
        stepsCompleted: nowComplete ? tut.steps.map((_, i) => i) : [],
      };
      const next = { ...prev, [tutId]: updated };
      saveProgress(next);
      return next;
    });
  };

  const roleInfo = ROLE_DISPLAY[role] ?? ROLE_DISPLAY.admin;

  // Quick ref sections for this role
  const quickRefVisible = QUICK_REF_SECTIONS.filter(s => s.roles.includes(role));

  return (
    <div style={{ padding: 24, background: atriumTokens.bg, minHeight: '100vh', fontFamily: atriumTokens.fontFamily }}>

      {/* ── Hero Banner ─────────────────────────────────────────── */}
      <div style={{
        background: atriumTokens.gradGreeting,
        borderRadius: atriumTokens.radiusBanner,
        padding: '28px 32px',
        marginBottom: 24,
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', right: -40, top: -40,
          width: 200, height: 200, borderRadius: '50%',
          background: 'rgba(255,255,255,0.04)',
        }} />
        <div style={{
          position: 'absolute', right: 60, bottom: -60,
          width: 150, height: 150, borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        }} />
        <Row align="middle" justify="space-between" gutter={24}>
          <Col>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10,
                background: 'rgba(255,255,255,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22,
              }}>
                <BookOutlined />
              </div>
              <div>
                <Title level={3} style={{ color: '#fff', margin: 0, fontSize: 22 }}>
                  Learning Centre
                </Title>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                  <Tag color={roleInfo.color} style={{ margin: 0, fontWeight: 600 }}>
                    {roleInfo.label}
                  </Tag>
                  <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
                    {roleInfo.description}
                  </Text>
                </div>
              </div>
            </div>
          </Col>
          <Col>
            <Row gutter={24}>
              {[
                { label: 'Total Modules',  value: totalCount,      color: '#fff' },
                { label: 'In Progress',    value: inProgressCount, color: '#FBBF24' },
                { label: 'Completed',      value: completedCount,  color: '#6EE7B7' },
                { label: 'Completion',     value: `${overallPct}%`, color: '#BFD3FF' },
              ].map(s => (
                <Col key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>
                    {s.value}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2, whiteSpace: 'nowrap' }}>
                    {s.label}
                  </div>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
        {overallPct > 0 && (
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>Overall Progress</Text>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>{overallPct}%</Text>
            </div>
            <Progress
              percent={overallPct}
              showInfo={false}
              strokeColor={{ from: '#818cf8', to: '#34d399' }}
              trailColor="rgba(255,255,255,0.1)"
            />
          </div>
        )}
      </div>

      {/* ── Main Tabs ────────────────────────────────────────────── */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        size="small"
        style={{ background: atriumTokens.card, borderRadius: atriumTokens.radiusCard, border: `1px solid ${atriumTokens.line}`, padding: '16px 16px 0' }}
        items={[
          // ── TAB 1: Learning Path ──────────────────────────────
          {
            key: 'path',
            label: (
              <span>
                <RocketOutlined style={{ marginRight: 5 }} />
                My Path
                <Badge
                  count={learningPath.length}
                  style={{ marginLeft: 5, background: atriumTokens.blue }}
                />
              </span>
            ),
            children: (
              <div style={{ padding: '16px 0' }}>
                <Alert
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  style={{ marginBottom: 20, fontSize: 13 }}
                  message={
                    <span>
                      This learning path is curated for the <strong>{roleInfo.label}</strong> role.
                      Complete modules in sequence for the best onboarding experience.
                      Check off individual steps as you complete them — progress is saved automatically.
                    </span>
                  }
                />
                {learningPath.map((tut, idx) => (
                  <div key={tut.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0,
                      paddingTop: 8,
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: progressMap[tut.id]?.completed ? atriumTokens.ok : CATEGORY_COLORS[tut.category] ?? atriumTokens.blue,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 700, fontSize: 12,
                      }}>
                        {progressMap[tut.id]?.completed ? <CheckOutlined style={{ fontSize: 11 }} /> : idx + 1}
                      </div>
                      {idx < learningPath.length - 1 && (
                        <div style={{ width: 2, flex: 1, minHeight: 20, background: atriumTokens.line2, marginTop: 4 }} />
                      )}
                    </div>
                    <div style={{ flex: 1, paddingBottom: 4 }}>
                      <TutorialCard
                        tutorial={tut}
                        progress={progressMap[tut.id] ?? { completed: false, stepsCompleted: [] }}
                        onStepToggle={handleStepToggle}
                        onMarkComplete={handleMarkComplete}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ),
          },

          // ── TAB 2: Course Library ─────────────────────────────
          {
            key: 'library',
            label: (
              <span>
                <ReadOutlined style={{ marginRight: 5 }} />
                Library
                <Badge
                  count={roleTutorials.length}
                  style={{ marginLeft: 5, background: atriumTokens.blue }}
                />
              </span>
            ),
            children: (
              <div style={{ padding: '16px 0' }}>
                {/* Filters */}
                <Card size="small" style={{ marginBottom: 16, borderRadius: atriumTokens.radiusCard }}>
                  <Row gutter={12} align="middle">
                    <Col flex="1">
                      <Input
                        prefix={<SearchOutlined style={{ color: atriumTokens.mut2 }} />}
                        placeholder="Search courses by title, description, or tag..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        allowClear
                      />
                    </Col>
                    <Col>
                      <Space>
                        <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Category:</Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {categories.map(cat => (
                            <Button
                              key={cat}
                              size="small"
                              type={selectedCategory === cat ? 'primary' : 'default'}
                              style={selectedCategory === cat ? {} : { fontSize: 12 }}
                              onClick={() => setSelectedCategory(cat)}
                            >
                              {cat === 'ALL' ? 'All Categories' : cat}
                            </Button>
                          ))}
                        </div>
                      </Space>
                    </Col>
                    <Col>
                      <Space>
                        <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Level:</Text>
                        {(['ALL', 'Beginner', 'Intermediate', 'Advanced'] as const).map(d => (
                          <Button
                            key={d}
                            size="small"
                            type={selectedDifficulty === d ? 'primary' : 'default'}
                            onClick={() => setSelectedDifficulty(d)}
                          >
                            {d === 'ALL' ? 'All Levels' : d}
                          </Button>
                        ))}
                      </Space>
                    </Col>
                  </Row>
                </Card>

                {filteredTutorials.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: atriumTokens.mut }}>
                    <BookOutlined style={{ fontSize: 48, marginBottom: 12, color: atriumTokens.line2 }} />
                    <div>No courses match your current filters.</div>
                  </div>
                ) : (
                  <>
                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
                      Showing {filteredTutorials.length} of {roleTutorials.length} courses
                    </Text>
                    {filteredTutorials.map(tut => (
                      <TutorialCard
                        key={tut.id}
                        tutorial={tut}
                        progress={progressMap[tut.id] ?? { completed: false, stepsCompleted: [] }}
                        onStepToggle={handleStepToggle}
                        onMarkComplete={handleMarkComplete}
                      />
                    ))}
                  </>
                )}
              </div>
            ),
          },

          // ── TAB 3: Video Demos ───────────────────────────────
          {
            key: 'videos',
            label: (
              <span>
                <VideoCameraOutlined style={{ marginRight: 5 }} />
                Video Demos
              </span>
            ),
            children: <VideoTutorials />,
          },

          // ── TAB 4: Quick Reference ────────────────────────────
          {
            key: 'reference',
            label: (
              <span>
                <StarOutlined style={{ marginRight: 5 }} />
                Quick Ref
              </span>
            ),
            children: (
              <div style={{ padding: '16px 0' }}>
                <Alert
                  type="success"
                  showIcon
                  icon={<BulbOutlined />}
                  style={{ marginBottom: 20, fontSize: 13 }}
                  message="Quick-reference guides, checklists, and templates for day-to-day operations. Bookmark this tab for rapid on-the-job lookups."
                />
                <Collapse accordion ghost>
                  {quickRefVisible.map((section, idx) => (
                    <Panel
                      key={idx}
                      header={
                        <Space>
                          <span style={{ color: atriumTokens.blue, fontSize: 16 }}>{section.icon}</span>
                          <Text strong style={{ fontSize: 14, color: atriumTokens.ink }}>{section.title}</Text>
                        </Space>
                      }
                      style={{
                        background: atriumTokens.card,
                        borderRadius: atriumTokens.radiusControl,
                        marginBottom: 8,
                        border: `1px solid ${atriumTokens.line}`,
                      }}
                    >
                      <div style={{ padding: '4px 0' }}>{section.content}</div>
                    </Panel>
                  ))}
                </Collapse>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
